import React, {useState, useEffect} from 'react';
import SeoTags from '../components/SeoTags';
import {withUserGetServerSideProps} from '../util/util';
import wrapper from '../store/store';
import {useRouter} from 'next/router';
import {AssessmentFormQuestion, FormProgressBar} from '../components/forms/AssessmentForm';
import {useAuth} from '../util/auth';
import {fetchAssessmentQuestions, postSelfAssessment} from '../util/assessment';
import {fetchMeta} from '../util/pages';
import {Typography} from '../components/ui/typography';
import {useAssessment} from '../util/hooks/useAssessment';
import {useStableCallback} from '../util/hooks/useStableCallback';
import {Tabs, TabsContent} from '../components/ui/tabs';
import {Card, CardContent, CardFooter, CardHeader, CardTitle} from '../components/ui/card';
import {Button} from '../components/ui/button';

function SelfAssessment() {
    const router = useRouter();
    const auth = useAuth();
    const [formSections, setFormSections] = useState([]);

    useEffect(() => {
        fetchAssessmentQuestions().then((response) => {
            if (response && response.length) {
                setFormSections(response);
            }
        }).catch(console.error);
    }, []);

    const {
        onChange: handleChange,
        formData,
        isValidForm,
        answers,
        clearAnswers,
        hasPreviousSection,
        hasNextSection,
        setCurrentTab,
        currentTab,
        progress,
        sectionsByExpertise,
    } = useAssessment({
        storageKey: `assessment-form`,
        formSections,
        tabsCount: 3,
    });

    const handleSubmit = useStableCallback(() => {
        if (!isValidForm) {
            return;
        }
        if (auth.isAuthenticated) {
            postSelfAssessment(formData).then(() => {
                clearAnswers();
                router.push('/self-assessment-result');
            }).catch(console.error);
        }
        router.push('/self-assessment-result');
    });


    return <>
        <SeoTags config={{
            title: 'Self-assessment'
        }} />

        <div>
            <div className="container">
                <div className="flex mt-6 mb-16">
                    <div className="w-full max-w-screen lg:w-1/2 m-auto">
                        <Typography variant="h2" className="mb-3">Self-assessment</Typography>
                        <div className="">
                            <div className="text-sm mb-6">
                                <p className="mb-2">Please use the scale 1 – 10, where:</p>
                                <p><b>1-2</b>: Strongly disagree</p>
                                <p><b>3-4</b>: Disagree</p>
                                <p><b>5-6</b>: Neutral</p>
                                <p><b>7-8</b>: Agree</p>
                                <p><b>9-10</b>: Strongly agree</p>
                                <p className="mt-2">Takes 15 minutes to complete, get your score immediately, and choose the right strategy for your training program.</p>
                            </div>
                            <FormProgressBar steps={[
                                ...(progress.map((step, index) => {
                                    return {
                                        active: Number(currentTab) === index,
                                        completed: step.completed
                                    }
                                })),
                            ]}/>
                            <Tabs value={currentTab} onValueChange={(value) => {
                                setCurrentTab(value);
                            }}>
                                {sectionsByExpertise.map((expertise, index) => {
                                    return <TabsContent key={expertise.id} value={String(index)}>
                                        <div className="flex justify-between items-center p-6 top-[98px] z-10">
                                            <Typography variant="h5">
                                                {expertise.title}
                                            </Typography>
                                        </div>
                                        <Card>
                                            <CardHeader>
                                                <CardTitle></CardTitle>
                                            </CardHeader>
                                            <CardContent className="space-y-2">
                                                {expertise.questions.map((question) => {
                                                    return (
                                                        <AssessmentFormQuestion
                                                            key={question.id}
                                                            selectedScore={answers[question.id]?.score}
                                                            question={question}
                                                            onChange={({value, checked}) => {
                                                                handleChange({
                                                                    id: question.id,
                                                                    score: value,
                                                                    checked,
                                                                    expertise_id: question.expertise_id
                                                                })
                                                            }}
                                                        />
                                                    )
                                                })}
                                            </CardContent>
                                            <CardFooter className="justify-between">
                                                {hasPreviousSection ? (
                                                    <Button variant="outline" size="sm" onClick={() => {
                                                        setCurrentTab(String(Number(currentTab) - 1));
                                                    }}>
                                                        Back
                                                    </Button>
                                                ) : null}
                                                {hasNextSection ? (
                                                    <Button variant="outline" size="sm" className="ml-auto" onClick={() => {
                                                        setCurrentTab(String(Number(currentTab) + 1));
                                                    }}>
                                                        Next
                                                    </Button>
                                                ) : null}
                                                {!hasNextSection ? (
                                                    <Button size="sm" onClick={handleSubmit} disabled={!isValidForm}>
                                                        Submit assessment
                                                    </Button>
                                                ) : null}
                                            </CardFooter>
                                        </Card>
                                    </TabsContent>
                                })}
                            </Tabs>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </>
}

export const getServerSideProps = wrapper.getServerSideProps(withUserGetServerSideProps(async (ctx) => {
    const content = await fetchMeta();
    return { props: {content} }
}));


export default SelfAssessment;
